/**
 * Shared slot types + formatting helpers for the public booking surface.
 *
 * Used by both the initial booking flow (BookPage) and the self-serve
 * reschedule picker (ManageBooking) so the two render available times
 * identically. All times come from the API as UTC ISO strings and are
 * displayed in the viewer's local timezone.
 */

export type Slot = { startUtc: string; endUtc: string };
export type SlotsResponse = { slots: Slot[]; duration_minutes: number };

export function browserTz(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

export function formatSlotLocal(iso: string, tz: string): string {
  return new Date(iso).toLocaleString('en-US', {
    timeZone: tz,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function dayKey(date: Date, tz: string): string {
  // 'en-CA' yields YYYY-MM-DD which is sortable + a stable group key.
  return date.toLocaleDateString('en-CA', { timeZone: tz });
}

export function groupSlotsByDay(slots: Slot[], tz: string): Map<string, Slot[]> {
  const groups = new Map<string, Slot[]>();
  for (const s of slots) {
    const key = dayKey(new Date(s.startUtc), tz);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(s);
  }
  return groups;
}
